/**
 * Single source of truth for the data the printed ID card actually uses.
 * Shared by the staff edit modal, the request table's print-ready check and
 * the print page itself, so all three agree on what "complete" means.
 *
 * See ID_CARD_REQUEST_PLAN.md §2c / §2d.
 */

export const CARD_FIELDS = [
    { name: 'name', label: 'Name', maxLength: 200 },
    { name: 'father_name', label: "Father's Name", maxLength: 100 },
    { name: 'roll_no', label: 'Roll No', maxLength: 50 },
    { name: 'course_code', label: 'Course', control: 'course', maxLength: 30 },
    { name: 'term', label: 'Semester / Year', control: 'term' },
    { name: 'date_of_birth', label: 'Date of Birth', control: 'date', maxLength: 50 },
    { name: 'phone', label: 'Contact', maxLength: 50 },
    { name: 'village', label: 'Village / Address', maxLength: 200 },
    { name: 'post_office', label: 'Post Office', maxLength: 100 },
    { name: 'pin', label: 'PIN', maxLength: 20 },
    { name: 'district', label: 'District', maxLength: 100 },
    { name: 'state', label: 'State', maxLength: 100 }
];

/** Email is a card field only in the sense that it is the identity key. */
export const IDENTITY_FIELDS = ['roll_no', 'email'];

export const TERM_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

/**
 * The photo that will print on the card.
 *
 * The student's own upload wins, for the same reason the request's text fields
 * win over the student record: it is the data they supplied for this card and
 * the office verified. `student_photo_url` is the STUDENT_PHOTO already on the
 * linked student record, which keeps requests raised before uploads existed
 * printable.
 */
export function getPhotoUrl(row) {
    if (!row) {
        return '';
    }

    if (Array.isArray(row.photo_file_url) && row.photo_file_url.length > 0) {
        return row.photo_file_url[0];
    }

    return row.student_photo_url ? row.student_photo_url : '';
}

export const DEFAULT_STATE = 'Assam';

/**
 * Which of the typed card fields are still blank. term = 0 counts as blank
 * because it produces a meaningless Class line.
 *
 * This is the check the Edit Details modal wants: its form holds only these
 * fields, and it cannot change the photo.
 */
export function missingTextFields(row) {
    if (!row) {
        return CARD_FIELDS.map(f => f.label);
    }

    return CARD_FIELDS
        .filter((f) => {
            let value = row[f.name];
            if (f.name === 'term') {
                return !value || parseInt(value, 10) < 1;
            }
            return value === undefined || value === null || String(value).trim() === '';
        })
        .map(f => f.label);
}

/**
 * Everything a request row is still missing before the card can be printed —
 * the typed fields plus the photo. The photo is not a CARD_FIELDS entry (it is
 * an upload, not a text value) but a card without one prints an empty photo
 * box, so it gates printing the same way.
 *
 * Only call this with a request row, never with the edit modal's form: the form
 * carries no photo keys and would always look short of one.
 */
export function missingCardFields(row) {
    let missing = missingTextFields(row);

    if (getPhotoUrl(row) === '') {
        missing = missing.concat(['Photo']);
    }

    return missing;
}

export function isPrintReady(row) {
    return missingCardFields(row).length === 0;
}

/**
 * Composes the Class line.
 *
 * The original version had no fallthrough on the HS branch, so any term outside
 * (1, 2) returned undefined and the card printed a blank Class line — which hit
 * the 37% of student rows that carry term = 0. Non-HS courses fared slightly
 * better but rendered a nonsense "0th Semester". Both are handled here, and
 * every input that already produced a sensible string still produces the same
 * string.
 */
export function getGoodClass(course_code, term) {
    if (!course_code) {
        return '';
    }

    const t = parseInt(term, 10);
    const isHigherSecondary = course_code === 'HSA' || course_code === 'HSS' || course_code === 'HSC';

    if (!t || t < 1) {
        return course_code;
    }

    if (isHigherSecondary) {
        if (t === 1) return course_code + ' 1st Year';
        if (t === 2) return course_code + ' 2nd Year';
        return `${course_code} Year ${t}`;
    }

    if (t === 1) return course_code + ' 1st Semester';
    if (t === 2) return course_code + ' 2nd Semester';
    if (t === 3) return course_code + ' 3rd Semester';
    return `${course_code} ${t}th Semester`;
}
